/**
 * PermissionGuard Component
 *
 * A generic component for protecting routes and components based on user roles.
 * This component implements Role-Based Access Control (RBAC) to ensure users
 * can only access routes they have permission for.
 *
 * @example
 * // Protect a route with automatic role-based permissions
 * <Route
 *   path="dashboard"
 *   element={
 *     <PermissionGuard>
 *       <Dashboard />
 *     </PermissionGuard>
 *   }
 * />
 *
 * @example
 * // Protect a route with specific required roles
 * <Route
 *   path="admin"
 *   element={
 *     <PermissionGuard requiredRoles={['Admin']}>
 *       <AdminPanel />
 *     </PermissionGuard>
 *   }
 * />
 *
 * @example
 * // Protect a component with custom fallback route
 * <PermissionGuard fallbackRoute="/unauthorized">
 *   <PremiumFeature />
 * </PermissionGuard>
 */

import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getDefaultRouteForUser,
  hasRouteAccessForUser,
  isValidUserRole,
  UserRole,
} from "../helpers/permissions";
import { getUser } from "../helpers/utils";

interface PermissionGuardProps {
  /** The component/route to protect */
  children: React.ReactNode;
  /** Specific roles required for access (optional, defaults to route-based permissions) */
  requiredRoles?: UserRole[];
  /** Custom fallback route if access denied (optional, defaults to user's default route) */
  fallbackRoute?: string;
  /** Whether to allow unauthenticated access (defaults to false) */
  allowUnauthenticated?: boolean;
}

/**
 * Generic PermissionGuard component for protecting routes and components
 *
 * This component automatically checks user permissions and redirects unauthorized users.
 * It supports both route-based permissions (from PERMISSIONS_CONFIG) and explicit role requirements.
 *
 * UX Behavior:
 * - Unauthenticated users → Redirect to login
 * - Invalid user roles → Redirect to dashboard (not logout)
 * - Insufficient permissions → Redirect to user's default route
 *
 * @param children - The component/route to protect
 * @param requiredRoles - Specific roles required (optional, defaults to route-based permissions)
 * @param fallbackRoute - Where to redirect if access denied (optional, defaults to user's default route)
 * @param allowUnauthenticated - Whether to allow unauthenticated access (defaults to false)
 *
 * @returns JSX element or null based on permission check
 */
export function PermissionGuard({
  children,
  requiredRoles,
  fallbackRoute,
  allowUnauthenticated = false,
}: PermissionGuardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser();
  const currentPath = location.pathname;
  const [isChecking, setIsChecking] = React.useState(true);

  // Check permissions immediately (synchronously) before any rendering
  const checkPermissions = React.useCallback(() => {
    // Handle unauthenticated users
    if (!user || !user.id) {
      if (!allowUnauthenticated) {
        navigate("/login", {
          state: { from: `${currentPath}${location.search}` },
          replace: true,
        });
        return false;
      }
      return true;
    }

    // Get user role
    const userRole = user.type as UserRole;

    // Validate user role
    if (!isValidUserRole(userRole)) {
      // If user is authenticated but has invalid role, redirect to dashboard
      // instead of logging them out - this provides better UX
      navigate("/dashboard", { replace: true });
      return false;
    }

    // Check permissions using specific roles or route-based permissions
    let hasAccess = false;

    if (requiredRoles) {
      // Use specific required roles
      hasAccess = requiredRoles.includes(userRole);
    } else {
      // Use route-based permissions (role grant minus the account-level
      // override, e.g. a hidden pricing dashboard)
      hasAccess = hasRouteAccessForUser(user, currentPath);
    }

    // Redirect if access denied
    if (!hasAccess) {
      const redirectRoute = fallbackRoute || getDefaultRouteForUser(user);
      navigate(redirectRoute, { replace: true });
      return false;
    }

    return true;
  }, [
    user,
    currentPath,
    navigate,
    location.search,
    requiredRoles,
    fallbackRoute,
    allowUnauthenticated,
  ]);

  useEffect(() => {
    checkPermissions();
    setIsChecking(false);
  }, [checkPermissions]);

  // Don't render anything while checking permissions or if access is denied
  if (isChecking) {
    return null;
  }

  // Show children only if user is authenticated and has access
  if (!user || !user.id) {
    return allowUnauthenticated ? <>{children}</> : null;
  }

  const userRole = user.type as UserRole;

  if (!isValidUserRole(userRole)) {
    return null;
  }

  // Check final access
  let hasAccess = false;

  if (requiredRoles) {
    hasAccess = requiredRoles.includes(userRole);
  } else {
    hasAccess = hasRouteAccessForUser(user, currentPath);
  }

  return hasAccess ? <>{children}</> : null;
}

/**
 * Hook for checking user permissions in components
 *
 * This hook provides a convenient way to check permissions within React components.
 * It returns the current user, their role, authentication status, and helper functions.
 *
 * @example
 * const { hasAccess, userRole, isAuthenticated } = usePermissions();
 *
 * if (hasAccess(['U', 'Admin'])) {
 *   return <AdminContent />;
 * }
 *
 * @returns Object containing user info, authentication status, and permission helpers
 */
export function usePermissions() {
  const user = getUser();
  const location = useLocation();

  const userRole = user?.type as UserRole;
  const currentPath = location.pathname;

  const hasAccess = (roles?: UserRole[]) => {
    if (!user || !user.id || !isValidUserRole(userRole)) {
      return false;
    }

    if (roles) {
      return roles.includes(userRole);
    }

    return hasRouteAccessForUser(user, currentPath);
  };

  const getDefaultRoute = () => {
    if (!user || !user.id || !isValidUserRole(userRole)) {
      return "/login";
    }
    return getDefaultRouteForUser(user);
  };

  const isAuthenticated = !!user && !!user.id;
  const isValidRole = isValidUserRole(userRole);

  return {
    user,
    userRole,
    isAuthenticated,
    isValidRole,
    hasAccess,
    getDefaultRoute,
    currentPath,
  };
}

/**
 * Higher-order component for protecting components with permissions
 *
 * This HOC wraps a component with PermissionGuard, providing a declarative way
 * to protect entire components with role-based access control.
 *
 * @example
 * const ProtectedAdminPanel = withPermissionGuard(AdminPanel, {
 *   requiredRoles: ['Admin'],
 *   fallbackRoute: '/unauthorized'
 * });
 *
 * @param Component - The component to protect
 * @param guardProps - PermissionGuard configuration options
 * @returns Wrapped component with permission protection
 */
export function withPermissionGuard<P extends object>(
  Component: React.ComponentType<P>,
  guardProps?: Omit<PermissionGuardProps, "children">
) {
  const WrappedComponent = (props: P) => (
    <PermissionGuard {...guardProps}>
      <Component {...props} />
    </PermissionGuard>
  );

  WrappedComponent.displayName = `withPermissionGuard(${
    Component.displayName || Component.name
  })`;

  return WrappedComponent;
}
